import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "../../../auth/AuthContext";
import { subscribeToNewsletter } from "../../../api/newsletter";
import { LocaleLink } from "../../../i18n/LocaleLink";
import "./Footer.less";

export function Footer() {
  const { t } = useTranslation("common");
  const { t: tn, i18n } = useTranslation("newsletter");
  const { user, isLoading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error" | "invalid" | "rate-limited">("idle");
  const showNewsletter = !user && !authLoading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus("invalid");
      return;
    }

    setStatus("loading");
    try {
      const result = await subscribeToNewsletter(trimmed, i18n.language);
      if (result.ok) {
        setStatus("success");
        setEmail("");
      } else if (result.error === "rate-limited") {
        setStatus("rate-limited");
      } else if (result.error === "invalid-email") {
        setStatus("invalid");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <footer className="site-footer" data-testid="site-footer">
      {showNewsletter && (
        <div className="site-footer__newsletter">
          <div className="site-footer__newsletter-inner">
            <span className="site-footer__newsletter-title">{tn("title")}</span>
            {status === "success" ? (
              <p className="site-footer__newsletter-success" data-testid="newsletter-success">
                {tn("success")}
              </p>
            ) : (
              <form className="site-footer__newsletter-form" onSubmit={handleSubmit} data-testid="newsletter-form">
                <div className="site-footer__newsletter-row">
                  <input
                    type="email"
                    className="site-footer__newsletter-input"
                    placeholder={tn("placeholder")}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status === "invalid" || status === "error" || status === "rate-limited") {
                        setStatus("idle");
                      }
                    }}
                    disabled={status === "loading"}
                    data-testid="newsletter-email"
                  />
                  <button
                    type="submit"
                    className="site-footer__newsletter-btn"
                    disabled={status === "loading"}
                    data-testid="newsletter-submit"
                  >
                    {status === "loading" ? tn("subscribing") : tn("subscribe")}
                  </button>
                </div>
                {status === "invalid" && (
                  <p className="site-footer__newsletter-error">{tn("invalidEmail")}</p>
                )}
                {status === "error" && (
                  <p className="site-footer__newsletter-error">{tn("error")}</p>
                )}
                {status === "rate-limited" && (
                  <p className="site-footer__newsletter-error">{tn("rateLimited")}</p>
                )}
                <p className="site-footer__newsletter-consent">
                  <Trans
                    i18nKey="consent"
                    ns="newsletter"
                    components={{ privacy: <LocaleLink to="/legal/privacy" /> }}
                  />
                </p>
              </form>
            )}
          </div>
        </div>
      )}
      <div className="site-footer__inner">
        <span className="site-footer__brand">Daynav</span>
        <nav className="site-footer__links">
          <LocaleLink to="/legal/imprint">{t("footer.imprint")}</LocaleLink>
          <LocaleLink to="/legal/cookies">{t("footer.cookies")}</LocaleLink>
          <LocaleLink to="/legal/privacy">{t("footer.privacy")}</LocaleLink>
          <LocaleLink to="/legal/liability">{t("footer.disclaimer")}</LocaleLink>
          <LocaleLink to="/about">{t("footer.about")}</LocaleLink>
          <LocaleLink to="/help">{t("footer.help")}</LocaleLink>
        </nav>
      </div>
      <div className="site-footer__copy">
        {t("footer.copyright", { year: __BUILD_YEAR__ })}
      </div>
    </footer>
  );
}
